import axios from 'axios';
import { API_URL } from '../config/api';

const walletService = {
  getWallets: async (token) => {
    const res = await axios.get(`${API_URL}/api/admin/wallets`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  },
  createWallet: async (token, user_id, initial_balance) => {
    try {
      // Validate inputs
      if (!user_id) {
        throw new Error('User ID is required');
      }
      
      const balance = parseFloat(initial_balance);
      if (isNaN(balance) || balance < 0) {
        throw new Error('Initial balance must be a valid number greater than or equal to 0');
      }

      const res = await axios.post(`${API_URL}/api/admin/wallets`, { 
        user_id, 
        initial_balance: balance 
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.data;
    } catch (error) {
      // Extract error message from response
      if (error.response) {
        const errorData = error.response.data;
        const errorMessage = errorData?.error || errorData?.message || errorData?.details?.[0]?.msg || `Request failed with status code ${error.response.status}`;
        throw new Error(errorMessage);
      }
      throw error;
    }
  },
  getWalletById: async (token, id) => {
    const res = await axios.get(`${API_URL}/api/admin/wallets/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  },
  getWalletDetailsByUserId: async (token, userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/wallet/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Log the response for debugging
      console.log('Wallet details API response:', res.data);
      
      // Backend returns { success: true, data: { wallet, balance, recentTransactions } }
      // Handle different response structures safely
      let responseData = null;
      
      if (res.data) {
        if (res.data.success !== undefined && res.data.data) {
          // Response has success field and data
          responseData = res.data.data;
        } else if (res.data.data) {
          // Response has nested data field
          responseData = res.data.data;
        } else {
          // Response is the data directly
          responseData = res.data;
        }
      }
      
      // Safely handle transactions in the response to prevent undefined errors
      if (responseData && typeof responseData === 'object') {
        // If recentTransactions exists but has issues, set it to empty array
        if (responseData.recentTransactions !== undefined && !Array.isArray(responseData.recentTransactions)) {
          // Try to safely extract transactions
          if (responseData.recentTransactions?.data?.transactions && Array.isArray(responseData.recentTransactions.data.transactions)) {
            responseData.recentTransactions = responseData.recentTransactions.data.transactions;
          } else {
            responseData.recentTransactions = [];
          }
        }
        
        // Ensure recentTransactions is always an array
        if (!responseData.recentTransactions) {
          responseData.recentTransactions = [];
        }
      }
      
      return responseData || {};
    } catch (error) {
      // Extract error message from response
      console.error('Error fetching wallet details:', error);
      if (error.response) {
        const errorData = error.response.data;
        console.error('Error response data:', errorData);
        
        // Check if the error message contains the transactions error
        let errorMessage = errorData?.error || errorData?.message;
        
        // If it's the transactions error, provide a more helpful message
        if (errorMessage && errorMessage.includes('transactions')) {
          errorMessage = 'Error loading transactions data. Wallet information may still be available.';
        } else if (!errorMessage) {
          errorMessage = `Request failed with status code ${error.response.status}`;
        }
        
        const err = new Error(errorMessage);
        err.response = error.response;
        throw err;
      }
      throw error;
    }
  },
  updateWallet: async (token, id, updates) => {
    const res = await axios.put(`${API_URL}/api/admin/wallets/${id}`, updates, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  },
  addMoney: async (token, walletId, amount, paymentReference) => {
    const res = await axios.post(`${API_URL}/internal/wallet/add-money`, {
      walletId,
      amount,
      paymentReference
    }, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'x-api-key': 'Ahmed'
      },
    });
    return res.data;
  },
  getWalletTransactions: async (token, walletId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/wallet/${walletId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Handle different response structures
      if (res.data && res.data.data) {
        return Array.isArray(res.data.data) ? res.data.data : (res.data.data.transactions || []);
      }
      return Array.isArray(res.data) ? res.data : [];
    } catch (error) {
      console.error('Error fetching wallet transactions:', error);
      // If the endpoint doesn't exist, try alternative endpoint
      if (error.response?.status === 404) {
        // Try getting transactions by user_id or wallet_id from transactions endpoint
        try {
          const allTransactions = await axios.get(`${API_URL}/api/admin/transactions?wallet_id=${walletId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (allTransactions.data && allTransactions.data.data) {
            return Array.isArray(allTransactions.data.data) 
              ? allTransactions.data.data 
              : (allTransactions.data.data.transactions || []);
          }
          return [];
        } catch (fallbackError) {
          console.error('Fallback transaction fetch failed:', fallbackError);
          return [];
        }
      }
      throw error;
    }
  },
  getDashboardStats: async (token) => {
    const res = await axios.get(`${API_URL}/api/admin/wallet/analytics`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data?.data || {};
  },
};

export default walletService; 